import { useState, useEffect, useRef } from 'react';
import { StickyNote, Loader2 } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Textarea } from '@/components/ui/textarea';
import { useUpdateInvoice } from '@/hooks/useInvoices';
import { cn } from '@/lib/utils';

// Inline notes editor for an invoice row in the billing table.
//
// Why this exists: the user can't always reflect a billing event 1:1 in the
// system. Example from the Case 3b workflow — scope shrunk after a final was
// already paid; the user refunds $100 to the customer outside the system.
// The invoice row stays as the historical record of what was billed, and the
// notes capture WHY ("refunded $100 via check 2026-06-01"). Internal-only —
// never appears on the customer-facing PDF.
//
// Behavior:
//   - Closed: icon button colored based on whether a note exists.
//   - Open: textarea pre-filled with current note; auto-saves on blur OR
//     when the popover closes. No explicit Save button to reduce friction.
//   - Optimistic close: we close immediately after the blur fires and let
//     the mutation run in the background; the toast is silenced (silent: true
//     on the mutation) because debounced keystroke commits are too noisy.

interface InvoiceNotesPopoverProps {
  invoiceId: string;
  jobId: string;
  notes: string | null;
}

export function InvoiceNotesPopover({ invoiceId, jobId, notes }: InvoiceNotesPopoverProps) {
  const updateInvoice = useUpdateInvoice(jobId);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(notes ?? '');
  // Track what we last committed so we don't fire a no-op PATCH when the
  // popover closes without changes.
  const committedRef = useRef<string>(notes ?? '');

  // External edits to `notes` (e.g. another tab) should overwrite the local
  // draft only when the popover isn't open — otherwise we'd clobber the user
  // mid-edit. Mirror committedRef too so the no-op check stays accurate.
  useEffect(() => {
    if (!open) {
      setDraft(notes ?? '');
      committedRef.current = notes ?? '';
    }
  }, [notes, open]);

  const commit = () => {
    const next = draft;
    if (next === committedRef.current) return;
    committedRef.current = next;
    updateInvoice.mutate({
      id: invoiceId,
      // Send null when the textarea is empty so DB stores a clean NULL
      // rather than an empty string — easier to filter against.
      notes: next.trim().length === 0 ? null : next,
      silent: true,
    });
  };

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      // Popover is closing → flush any pending edits.
      commit();
    }
    setOpen(next);
  };

  const hasNote = (notes ?? '').trim().length > 0;

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={hasNote ? 'Edit invoice note' : 'Add invoice note'}
          title={hasNote ? notes ?? '' : 'Add a note'}
          className={cn(
            'flex w-full items-center gap-1.5 rounded px-2 py-1 text-left text-sm hover:bg-gray-100 transition-colors',
            hasNote ? 'text-gray-700' : 'text-gray-400',
          )}
        >
          {!hasNote && <StickyNote className="h-3.5 w-3.5 shrink-0 text-gray-300" />}
          <span className="truncate">{hasNote ? notes : 'Add note…'}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-3">
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-gray-700">
              Internal note
            </label>
            {updateInvoice.isPending && (
              <Loader2 className="h-3 w-3 animate-spin text-gray-400" />
            )}
          </div>
          <Textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onBlur={commit}
            placeholder="e.g. refunded $100 via check 2026-06-01"
            className="min-h-[80px] text-sm"
            autoFocus
          />
          <p className="text-[11px] text-gray-400">
            Not shown on the customer&apos;s invoice PDF.
          </p>
        </div>
      </PopoverContent>
    </Popover>
  );
}
